import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  Building2,
  Check,
  Database,
  Globe,
  Home,
  ListFilter,
  Loader2,
  Lock,
  Mail,
  Plug,
  Plus,
  RefreshCw,
  Shield,
  Trash2,
  UserPlus,
  Users,
  Webhook,
  X,
  ArrowLeft,
  Wifi,
  WifiOff,
} from "lucide-react";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AutomationsCard } from "@/components/settings/AutomationsCard";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import type {
  Organization,
  OrganizationApiSecurityPolicy,
  OrganizationMember,
} from "@/stores/organization/organization-store";
import { api } from "@/services/api/client";
import * as errorUtils from "@/utils/errors";
import {
  mergeSecurityPolicy,
  normalizeAllowedDomain,
  SECURITY_POLICY_LIMITS,
  validateSecurityPolicy,
} from "@/pages/orgSettingsSecurity";
import {
  canChangeOrganizationMemberRoles,
  canManageOrganization,
  canManageOrganizationMembers,
} from "@/utils/organizationPermissions";
import {
  callService,
  deleteEntityName,
  getEntities,
  getEntityNames,
  getInstances,
  registerInstance,
  setEntityName,
  type HAEntity,
  type HAEntityName,
  type HAInstance,
} from "@/services/api/haApi";
import { Slider } from "@/components/ui/slider";

type OrgSettingsTab =
  | "general"
  | "members"
  | "security"
  | "automations"
  | "integrations";

type InviteRole = "admin" | "member" | "viewer";
type EditableRole = "admin" | "member" | "viewer";

export interface OrganizationDetails extends Organization {
  securityPolicies?: OrganizationApiSecurityPolicy;
}

interface UpdateSecurityPolicyRequest {
  securityPolicy: OrganizationApiSecurityPolicy;
}

interface UpdateGeneralRequest {
  name?: string;
  description?: string;
}

const TABS: Array<{ value: OrgSettingsTab; label: string }> = [
  { value: "general", label: "General" },
  { value: "members", label: "Members" },
  { value: "security", label: "Security" },
  { value: "automations", label: "Automations" },
  { value: "integrations", label: "Integrations" },
];

const INVITE_ROLES: InviteRole[] = ["admin", "member", "viewer"];
const EDITABLE_ROLES: EditableRole[] = ["admin", "member", "viewer"];

const isOrgSettingsTab = (value: string): value is OrgSettingsTab =>
  TABS.some((tab) => tab.value === value);

const isInviteRole = (value: string): value is InviteRole =>
  value === "admin" || value === "member" || value === "viewer";

const isEditableRole = (value: string): value is EditableRole =>
  value === "admin" || value === "member" || value === "viewer";

const formatDate = (value?: string): string => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatEnumValue = (value?: string): string =>
  value
    ? value
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "Not available";

const roleBadgeVariant = (
  role: string,
): "default" | "secondary" | "outline" => {
  if (role === "owner" || role === "admin") return "default";
  if (role === "member") return "secondary";
  return "outline";
};

export const OrgSettingsPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organizations, fetchOrganizations, switchToOrgContext } =
    useOrganizationStore();
  const [organization, setOrganization] = useState<OrganizationDetails | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const currentTabParam = searchParams.get("tab") || "general";
  const currentTab = isOrgSettingsTab(currentTabParam)
    ? currentTabParam
    : "general";
  const selectedIntegration =
    currentTab === "integrations" ? searchParams.get("integration") : null;

  const storeOrganization = useMemo(
    () => organizations.find((org) => org.slug === orgSlug),
    [organizations, orgSlug],
  );
  const activeOrganization = organization ?? storeOrganization ?? null;
  const membershipRole = activeOrganization?.membership?.role;
  const canManage = canManageOrganization(membershipRole);

  useEffect(() => {
    if (!orgSlug) return;

    let isMounted = true;

    const loadOrganization = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        await fetchOrganizations();
        const response = await api.get<OrganizationDetails>(
          `/organizations/${orgSlug}`,
        );
        if (!response.data) {
          throw new Error("Organization not found");
        }
        if (isMounted) {
          setOrganization(response.data);
          switchToOrgContext(orgSlug);
        }
      } catch (error: unknown) {
        if (isMounted) {
          setLoadError(
            errorUtils.getApiErrorMessage(
              error,
              "Failed to load organization settings",
            ),
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOrganization();

    return () => {
      isMounted = false;
    };
  }, [fetchOrganizations, orgSlug, switchToOrgContext]);

  const handleTabChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value === "general") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", value);
    }
    nextParams.delete("integration");
    setSearchParams(nextParams);
  };

  const handleManageIntegration = (integrationId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "integrations");
    nextParams.set("integration", integrationId);
    setSearchParams(nextParams);
  };

  const handleBackToIntegrationRegistry = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "integrations");
    nextParams.delete("integration");
    setSearchParams(nextParams);
  };

  const handleOrganizationUpdated = (updated: OrganizationDetails) => {
    setOrganization(updated);
  };

  if (!orgSlug) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading organization settings...
      </div>
    );
  }

  if (loadError || !activeOrganization) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Organization unavailable</CardTitle>
          <CardDescription>
            {loadError ?? "This organization could not be loaded."}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!canManage) {
    return <Navigate to={`/orgs/${orgSlug}/projects`} replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <Badge variant={roleBadgeVariant(membershipRole ?? "")}>
              {membershipRole}
            </Badge>
          </div>
          <h1 className="text-3xl font-bold">Organization settings</h1>
          <p className="text-muted-foreground">
            Manage {activeOrganization.name} settings, access, and automation.
          </p>
        </div>
      </div>

      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="grid h-auto w-full grid-cols-2 rounded-lg bg-muted p-1 lg:grid-cols-5">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="rounded-md py-2.5 transition-all data-[state=active]:bg-primary data-[state=active]:font-semibold data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general" className="mt-6">
          <GeneralTab
            organization={activeOrganization}
            orgSlug={orgSlug}
            onOrganizationUpdated={handleOrganizationUpdated}
          />
        </TabsContent>
        <TabsContent value="members" className="mt-6">
          <MembersTab organization={activeOrganization} />
        </TabsContent>
        <TabsContent value="security" className="mt-6">
          <SecurityTab
            orgSlug={orgSlug}
            organization={activeOrganization}
            onOrganizationUpdated={handleOrganizationUpdated}
          />
        </TabsContent>
        <TabsContent value="automations" className="mt-6">
          <AutomationsTab orgSlug={orgSlug} />
        </TabsContent>
        <TabsContent value="integrations" className="mt-6">
          {selectedIntegration === "homeassistant" ? (
            <HomeAssistantManagementShell
              organization={activeOrganization}
              onBack={handleBackToIntegrationRegistry}
            />
          ) : (
            <IntegrationsTab
              organization={activeOrganization}
              onManageIntegration={handleManageIntegration}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface GeneralTabProps {
  organization: OrganizationDetails;
  orgSlug: string;
  onOrganizationUpdated: (organization: OrganizationDetails) => void;
}

const GeneralTab: React.FC<GeneralTabProps> = ({
  organization,
  orgSlug,
  onOrganizationUpdated,
}) => {
  const [name, setName] = useState(organization.name);
  const [description, setDescription] = useState(
    organization.description ?? "",
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setName(organization.name);
    setDescription(organization.description ?? "");
  }, [organization.description, organization.name]);

  const hasChanges =
    name.trim() !== organization.name ||
    description.trim() !== (organization.description ?? "");

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Organization name is required");
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.patch<
        OrganizationDetails,
        UpdateGeneralRequest
      >(`/organizations/${orgSlug}`, {
        name: name.trim(),
        description: description.trim(),
      });
      if (!response.data) {
        throw new Error("No organization returned");
      }
      onOrganizationUpdated(response.data);
      toast.success("Organization details updated");
    } catch (error: unknown) {
      toast.error("Failed to update organization", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            General
          </CardTitle>
          <CardDescription>
            Core organization identity and profile details.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-2">
            <Label htmlFor="org-name">Name</Label>
            <Input
              id="org-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="org-description">Description</Label>
            <Textarea
              id="org-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className="min-h-24 resize-none"
              placeholder="What this organization manages"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="org-slug">Slug</Label>
            <Input id="org-slug" value={organization.slug} readOnly />
          </div>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            Save changes
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Organization metadata</CardTitle>
          <CardDescription>
            Read-only organization lifecycle details.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MetadataRow
            label="Plan tier"
            value={formatEnumValue(organization.billingPlan?.type)}
          />
          <MetadataRow
            label="Billing status"
            value={formatEnumValue(organization.billingPlan?.status)}
          />
          <MetadataRow
            label="Current period ends"
            value={formatDate(organization.billingPlan?.currentPeriodEnd)}
          />
          <MetadataRow
            label="Created"
            value={formatDate(organization.createdAt)}
          />
          <MetadataRow
            label="Updated"
            value={formatDate(organization.updatedAt)}
          />
        </CardContent>
      </Card>
    </div>
  );
};

const MetadataRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-4 border-b pb-3 last:border-b-0 last:pb-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium capitalize">{value}</span>
  </div>
);

const MembersTab: React.FC<{ organization: OrganizationDetails }> = ({
  organization,
}) => {
  const {
    getMembers,
    inviteMember,
    updateMemberRole,
    removeMember,
    resendInvitation,
    cancelInvitation,
  } = useOrganizationStore();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<InviteRole>("member");
  const [isInviting, setIsInviting] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [resendingMemberId, setResendingMemberId] = useState<string | null>(
    null,
  );
  const [memberToRemove, setMemberToRemove] =
    useState<OrganizationMember | null>(null);
  const [memberToCancel, setMemberToCancel] =
    useState<OrganizationMember | null>(null);

  const loadMembers = useCallback(async () => {
    setIsLoading(true);
    try {
      setMembers(await getMembers(organization.slug));
    } catch (error: unknown) {
      toast.error("Failed to load members", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setIsLoading(false);
    }
  }, [getMembers, organization.slug]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const activeMembers = members.filter(
    (member) => member.status === "active" || !member.status,
  );
  const pendingMembers = members.filter(
    (member) => member.status === "pending" || member.status === "invited",
  );
  const viewerRole = organization.membership?.role;
  const canManageMembers = canManageOrganizationMembers(viewerRole);
  const canChangeRoles = canChangeOrganizationMemberRoles(viewerRole);

  const handleInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    try {
      const result = await inviteMember(organization.slug, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      toast.success(
        result.emailSent ? "Invitation sent" : "Invitation created",
        {
          description: `${result.email} can join ${organization.name}.`,
        },
      );
      setInviteEmail("");
      setInviteRole("member");
      await loadMembers();
    } catch (error: unknown) {
      toast.error("Failed to invite member", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleRoleChange = async (
    member: OrganizationMember,
    nextRole: EditableRole,
  ) => {
    const memberId = member.membershipId ?? member.userId;
    setUpdatingMemberId(memberId);
    try {
      await updateMemberRole(organization.slug, memberId, nextRole);
      toast.success("Member role updated");
      await loadMembers();
    } catch (error: unknown) {
      toast.error("Failed to update role", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const confirmRemove = async () => {
    if (!memberToRemove) return;
    const memberId = memberToRemove.membershipId ?? memberToRemove.userId;
    setUpdatingMemberId(memberId);
    try {
      await removeMember(organization.slug, memberId);
      toast.success("Member removed");
      setMemberToRemove(null);
      await loadMembers();
    } catch (error: unknown) {
      toast.error("Failed to remove member", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleResend = async (member: OrganizationMember) => {
    if (!member.membershipId) {
      toast.error("Missing invitation ID");
      return;
    }
    setResendingMemberId(member.membershipId);
    try {
      await resendInvitation(organization.slug, member.membershipId);
      toast.success("Invitation resent");
      await loadMembers();
    } catch (error: unknown) {
      toast.error("Failed to resend invitation", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setResendingMemberId(null);
    }
  };

  const confirmCancelInvite = async () => {
    if (!memberToCancel?.membershipId) return;
    try {
      await cancelInvitation(organization.slug, memberToCancel.membershipId);
      toast.success("Invitation cancelled");
      setMemberToCancel(null);
      await loadMembers();
    } catch (error: unknown) {
      toast.error("Failed to cancel invitation", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    }
  };

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px)_1fr]">
        {canManageMembers && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Invite member
              </CardTitle>
              <CardDescription>
                Add a teammate to {organization.name}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleInvite}>
                <div className="grid gap-2">
                  <Label htmlFor="invite-email">Email</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(event) => setInviteEmail(event.target.value)}
                    placeholder="teammate@example.com"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="invite-role">Role</Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(value) => {
                      if (isInviteRole(value)) setInviteRole(value);
                    }}
                  >
                    <SelectTrigger id="invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INVITE_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          <span className="capitalize">{role}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isInviting || !inviteEmail.trim()}
                >
                  {isInviting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="mr-2 h-4 w-4" />
                  )}
                  Send invitation
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Members
                </CardTitle>
                <CardDescription>
                  {activeMembers.length} active, {pendingMembers.length}{" "}
                  pending.
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadMembers}
                disabled={isLoading}
                aria-label="Refresh members"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!canChangeRoles && (
              <div
                className="mb-4 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground"
                role="note"
              >
                Only organization owners can change member roles. Admins can
                invite members, remove non-owner members, and manage pending
                invitations.
              </div>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading members...
              </div>
            ) : (
              <div className="space-y-6">
                <MemberTable
                  title="Active members"
                  members={activeMembers}
                  updatingMemberId={updatingMemberId}
                  canChangeRoles={canChangeRoles}
                  canRemoveMembers={canManageMembers}
                  onRoleChange={handleRoleChange}
                  onRemove={setMemberToRemove}
                />
                {pendingMembers.length > 0 && (
                  <PendingInviteList
                    members={pendingMembers}
                    resendingMemberId={resendingMemberId}
                    canManageInvitations={canManageMembers}
                    onResend={handleResend}
                    onCancel={setMemberToCancel}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={Boolean(memberToRemove)}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {memberToRemove?.name || memberToRemove?.email} from{" "}
              {organization.name}? They will lose organization access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmRemove}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(memberToCancel)}
        onOpenChange={(open) => {
          if (!open) setMemberToCancel(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Cancel the invitation for {memberToCancel?.email}? Their link will
              stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep invitation</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelInvite}>
              Cancel invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

interface MemberTableProps {
  title: string;
  members: OrganizationMember[];
  updatingMemberId: string | null;
  canChangeRoles: boolean;
  canRemoveMembers: boolean;
  onRoleChange: (member: OrganizationMember, role: EditableRole) => void;
  onRemove: (member: OrganizationMember) => void;
}

const MemberTable: React.FC<MemberTableProps> = ({
  title,
  members,
  updatingMemberId,
  canChangeRoles,
  canRemoveMembers,
  onRoleChange,
  onRemove,
}) => {
  if (members.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        No active members found.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => {
            const memberId = member.membershipId ?? member.userId;
            const isUpdating = updatingMemberId === memberId;

            return (
              <TableRow key={memberId}>
                <TableCell>
                  <div className="font-medium">
                    {member.name || member.email}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {member.email}
                  </div>
                </TableCell>
                <TableCell>
                  {canChangeRoles && member.role !== "owner" ? (
                    <Select
                      value={member.role}
                      disabled={isUpdating}
                      onValueChange={(value) => {
                        if (isEditableRole(value)) onRoleChange(member, value);
                      }}
                    >
                      <SelectTrigger
                        className="h-8 w-32"
                        aria-label={`Change role for ${member.name || member.email}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EDITABLE_ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            <span className="capitalize">{role}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge
                      variant={roleBadgeVariant(member.role)}
                      className="capitalize"
                    >
                      {member.role}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">
                    {member.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {canRemoveMembers && member.role !== "owner" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemove(member)}
                      disabled={isUpdating}
                      aria-label={`Remove ${member.name || member.email}`}
                    >
                      {isUpdating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

interface PendingInviteListProps {
  members: OrganizationMember[];
  resendingMemberId: string | null;
  canManageInvitations: boolean;
  onResend: (member: OrganizationMember) => void;
  onCancel: (member: OrganizationMember) => void;
}

const PendingInviteList: React.FC<PendingInviteListProps> = ({
  members,
  resendingMemberId,
  canManageInvitations,
  onResend,
  onCancel,
}) => (
  <div className="space-y-3">
    <h3 className="text-sm font-semibold">Pending invitations</h3>
    <div className="space-y-2">
      {members.map((member) => (
        <div
          key={member.membershipId ?? member.userId}
          className="flex flex-col gap-3 rounded-md border border-dashed p-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <div className="font-medium">{member.email}</div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Invited {formatDate(member.invitedAt)}</span>
              {member.expiresAt && (
                <span>Expires {formatDate(member.expiresAt)}</span>
              )}
              <Badge
                variant={roleBadgeVariant(member.role)}
                className="capitalize"
              >
                {member.role}
              </Badge>
            </div>
          </div>
          {canManageInvitations && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResend(member)}
                disabled={resendingMemberId === member.membershipId}
              >
                {resendingMemberId === member.membershipId ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Resend
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCancel(member)}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

interface SecurityTabProps {
  orgSlug: string;
  organization: OrganizationDetails;
  onOrganizationUpdated: (organization: OrganizationDetails) => void;
}

export const SecurityTab: React.FC<SecurityTabProps> = ({
  orgSlug,
  organization,
  onOrganizationUpdated,
}) => {
  const [securityPolicy, setSecurityPolicy] =
    useState<OrganizationApiSecurityPolicy>(
      mergeSecurityPolicy(
        organization.securityPolicy ?? organization.securityPolicies,
      ),
    );
  const [domainInput, setDomainInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSecurityPolicy(
      mergeSecurityPolicy(
        organization.securityPolicy ?? organization.securityPolicies,
      ),
    );
  }, [organization.securityPolicies, organization.securityPolicy]);

  const updateSecurityPolicy = (
    updates: Partial<OrganizationApiSecurityPolicy>,
  ) => {
    setSecurityPolicy((current) => ({
      ...current,
      ...updates,
    }));
  };

  const updatePasswordPolicy = (
    passwordPolicy: Partial<OrganizationApiSecurityPolicy["passwordPolicy"]>,
  ) => {
    setSecurityPolicy((current) => ({
      ...current,
      passwordPolicy: {
        ...current.passwordPolicy,
        ...passwordPolicy,
      },
    }));
  };

  const addDomain = () => {
    const domain = normalizeAllowedDomain(domainInput);
    if (!domain) {
      toast.error("Enter a valid domain without a protocol or path");
      return;
    }
    if (securityPolicy.allowedDomains.includes(domain)) {
      setDomainInput("");
      return;
    }
    updateSecurityPolicy({
      allowedDomains: [...securityPolicy.allowedDomains, domain],
    });
    setDomainInput("");
  };

  const removeDomain = (domain: string) => {
    updateSecurityPolicy({
      allowedDomains: securityPolicy.allowedDomains.filter(
        (existing) => existing !== domain,
      ),
    });
  };

  const handleSave = async () => {
    const validationErrors = validateSecurityPolicy(securityPolicy);
    if (validationErrors.length > 0) {
      toast.error("Fix security policy values", {
        description: validationErrors[0],
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.patch<
        OrganizationDetails,
        UpdateSecurityPolicyRequest
      >(`/organizations/${orgSlug}`, { securityPolicy });
      if (!response.data) {
        throw new Error("No organization returned");
      }
      onOrganizationUpdated(response.data);
      toast.success("Security settings updated");
    } catch (error: unknown) {
      toast.error("Failed to update security settings", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Access controls
          </CardTitle>
          <CardDescription>
            Configure MFA, domain restrictions, and session lifetime.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <SettingSwitch
            icon={<Lock className="h-4 w-4" />}
            label="Require MFA"
            description="Require multi-factor authentication for organization members."
            checked={securityPolicy.mfaRequired}
            onCheckedChange={(checked) =>
              updateSecurityPolicy({ mfaRequired: checked })
            }
          />
          <div className="grid gap-2">
            <Label htmlFor="session-timeout">Session timeout</Label>
            <div className="flex items-center gap-2">
              <Input
                id="session-timeout"
                type="number"
                min={SECURITY_POLICY_LIMITS.sessionTimeout.min}
                max={SECURITY_POLICY_LIMITS.sessionTimeout.max}
                value={securityPolicy.sessionTimeout}
                onChange={(event) =>
                  updateSecurityPolicy({
                    sessionTimeout: Number(event.target.value),
                  })
                }
              />
              <span className="text-sm text-muted-foreground">seconds</span>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="allowed-domain">Allowed domains</Label>
            <div className="flex gap-2">
              <Input
                id="allowed-domain"
                value={domainInput}
                onChange={(event) => setDomainInput(event.target.value)}
                placeholder="example.com"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addDomain();
                  }
                }}
              />
              <Button type="button" onClick={addDomain} variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {securityPolicy.allowedDomains.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No domain restrictions configured.
                </span>
              ) : (
                securityPolicy.allowedDomains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    <Globe className="h-3 w-3" />
                    {domain}
                    <button
                      type="button"
                      onClick={() => removeDomain(domain)}
                      aria-label={`Remove ${domain}`}
                      className="rounded p-0.5 hover:bg-background/70"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Password policy</CardTitle>
          <CardDescription>
            Define minimum password requirements for organization accounts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-2">
            <Label htmlFor="password-min-length">Minimum length</Label>
            <Input
              id="password-min-length"
              type="number"
              min={SECURITY_POLICY_LIMITS.passwordMinLength.min}
              max={SECURITY_POLICY_LIMITS.passwordMinLength.max}
              value={securityPolicy.passwordPolicy.minLength}
              onChange={(event) =>
                updatePasswordPolicy({ minLength: Number(event.target.value) })
              }
            />
          </div>
          <div className="grid gap-3">
            <SettingSwitch
              label="Uppercase letters"
              description="Require at least one uppercase character."
              checked={securityPolicy.passwordPolicy.requireUppercase}
              onCheckedChange={(checked) =>
                updatePasswordPolicy({ requireUppercase: checked })
              }
            />
            <SettingSwitch
              label="Lowercase letters"
              description="Require at least one lowercase character."
              checked={securityPolicy.passwordPolicy.requireLowercase}
              onCheckedChange={(checked) =>
                updatePasswordPolicy({ requireLowercase: checked })
              }
            />
            <SettingSwitch
              label="Numbers"
              description="Require at least one number."
              checked={securityPolicy.passwordPolicy.requireNumbers}
              onCheckedChange={(checked) =>
                updatePasswordPolicy({ requireNumbers: checked })
              }
            />
            <SettingSwitch
              label="Special characters"
              description="Require punctuation or symbols."
              checked={securityPolicy.passwordPolicy.requireSpecialChars}
              onCheckedChange={(checked) =>
                updatePasswordPolicy({ requireSpecialChars: checked })
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password-expiration-days">
              Password expiration
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="password-expiration-days"
                type="number"
                min={SECURITY_POLICY_LIMITS.passwordExpirationDays.min}
                max={SECURITY_POLICY_LIMITS.passwordExpirationDays.max}
                value={securityPolicy.passwordPolicy.expirationDays}
                onChange={(event) =>
                  updatePasswordPolicy({
                    expirationDays: Number(event.target.value),
                  })
                }
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password-prevent-reuse">
              Prevent password reuse
            </Label>
            <Input
              id="password-prevent-reuse"
              type="number"
              min={SECURITY_POLICY_LIMITS.passwordPreventReuse.min}
              max={SECURITY_POLICY_LIMITS.passwordPreventReuse.max}
              value={securityPolicy.passwordPolicy.preventReuse}
              onChange={(event) =>
                updatePasswordPolicy({
                  preventReuse: Number(event.target.value),
                })
              }
            />
          </div>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            Save security settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

interface SettingSwitchProps {
  icon?: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const SettingSwitch: React.FC<SettingSwitchProps> = ({
  icon,
  label,
  description,
  checked,
  onCheckedChange,
}) => (
  <div className="flex items-start justify-between gap-4 rounded-md border p-4">
    <div className="flex gap-3">
      {icon && <div className="mt-0.5 text-muted-foreground">{icon}</div>}
      <div>
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

interface AutomationsTabProps {
  orgSlug: string;
}

export const AutomationsTab: React.FC<AutomationsTabProps> = ({ orgSlug }) => (
  <div className="grid gap-6">
    <AutomationsCard orgSlug={orgSlug} mode="org" />
  </div>
);

interface IntegrationsTabProps {
  organization: OrganizationDetails;
  onManageIntegration: (integrationId: string) => void;
}

interface IntegrationRegistryItem {
  id: string;
  name: string;
  description: string;
  status: "configured" | "available" | "coming-soon";
  icon: React.ReactNode;
  metadata: string[];
}

const formatRelativeIntegrationDate = (value?: string | null): string => {
  if (!value) return "Not seen yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not seen yet";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getHomeAssistantStatus = (
  instances: HAInstance[],
): IntegrationRegistryItem["status"] =>
  instances.length > 0 ? "configured" : "available";

const getHomeAssistantMetadata = (instances: HAInstance[]): string[] => {
  if (instances.length === 0) {
    return ["No instances connected", "Org-scoped setup"];
  }

  const onlineCount = instances.filter(
    (instance) => instance.status === "online",
  ).length;
  const entityCount = instances.reduce(
    (total, instance) => total + (instance.entityCount || 0),
    0,
  );
  const lastSeenValues = instances
    .map((instance) => instance.lastSeen)
    .filter((value): value is string => Boolean(value));
  const latestLastSeen = lastSeenValues
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((left, right) => right.getTime() - left.getTime())[0];

  return [
    `${onlineCount}/${instances.length} online`,
    `${entityCount} entities`,
    `Last seen ${formatRelativeIntegrationDate(latestLastSeen?.toISOString())}`,
  ];
};

export const IntegrationsTab: React.FC<IntegrationsTabProps> = ({
  organization,
  onManageIntegration,
}) => {
  const [haInstances, setHaInstances] = useState<HAInstance[]>([]);
  const [isLoadingHomeAssistant, setIsLoadingHomeAssistant] = useState(true);
  const [homeAssistantError, setHomeAssistantError] = useState<string | null>(
    null,
  );

  const loadHomeAssistantSummary = useCallback(async () => {
    setIsLoadingHomeAssistant(true);
    setHomeAssistantError(null);
    try {
      setHaInstances(await getInstances());
    } catch (error: unknown) {
      setHomeAssistantError(
        errorUtils.getApiErrorMessage(
          error,
          "Failed to load Home Assistant status",
        ),
      );
    } finally {
      setIsLoadingHomeAssistant(false);
    }
  }, []);

  useEffect(() => {
    loadHomeAssistantSummary();
  }, [loadHomeAssistantSummary, organization.slug]);

  const registryItems: IntegrationRegistryItem[] = [
    {
      id: "homeassistant",
      name: "Home Assistant",
      description:
        "Connect organization-scoped Home Assistant instances, entities, devices, and automations.",
      status: getHomeAssistantStatus(haInstances),
      icon:
        haInstances.some((instance) => instance.status === "online") ? (
          <Wifi className="h-5 w-5" />
        ) : (
          <Home className="h-5 w-5" />
        ),
      metadata: getHomeAssistantMetadata(haInstances),
    },
    {
      id: "kasa",
      name: "Kasa / Tapo",
      description: "Manage TP-Link smart plugs, switches, and energy devices.",
      status: "available",
      icon: <Plug className="h-5 w-5" />,
      metadata: ["Device controls", "Energy readings"],
    },
    {
      id: "unifi-network",
      name: "UniFi Network",
      description: "Connect network presence, clients, and infrastructure state.",
      status: "available",
      icon: <Globe className="h-5 w-5" />,
      metadata: ["Presence", "Network health"],
    },
    {
      id: "unifi-protect",
      name: "UniFi Protect",
      description: "Connect cameras, NVR health, motion events, and livestreams.",
      status: "available",
      icon: <Shield className="h-5 w-5" />,
      metadata: ["Cameras", "Events"],
    },
    {
      id: "wazuh",
      name: "Wazuh",
      description: "Security monitoring and alerting integration planned for orgs.",
      status: "coming-soon",
      icon: <Lock className="h-5 w-5" />,
      metadata: ["Security events", "Coming soon"],
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5" />
            Organization integrations
          </CardTitle>
          <CardDescription>
            Manage integrations for {organization.name}. Each integration is
            scoped to this organization and cannot expose another
            organization's connected systems.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {registryItems.map((item) => (
            <IntegrationRegistryCard
              key={item.id}
              item={item}
              isLoading={item.id === "homeassistant" && isLoadingHomeAssistant}
              error={item.id === "homeassistant" ? homeAssistantError : null}
              onManage={
                item.id === "homeassistant"
                  ? () => onManageIntegration(item.id)
                  : undefined
              }
              onRefresh={
                item.id === "homeassistant"
                  ? loadHomeAssistantSummary
                  : undefined
              }
            />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Integration roadmap
          </CardTitle>
          <CardDescription>
            Home Assistant management starts with entity discovery, then expands
            into permissions, diagnostics, and Page Builder blocks.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          <IntegrationPlaceholder
            icon={<Home className="h-4 w-4" />}
            label="HA entities"
          />
          <IntegrationPlaceholder
            icon={<Shield className="h-4 w-4" />}
            label="Permissions"
          />
          <IntegrationPlaceholder
            icon={<Webhook className="h-4 w-4" />}
            label="Diagnostics"
          />
        </CardContent>
      </Card>
    </div>
  );
};

const IntegrationRegistryCard: React.FC<{
  item: IntegrationRegistryItem;
  isLoading: boolean;
  error: string | null;
  onManage?: () => void;
  onRefresh?: () => void;
}> = ({ item, isLoading, error, onManage, onRefresh }) => (
  <div className="flex h-full flex-col rounded-lg border bg-card p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <div className="rounded-md bg-muted p-2 text-muted-foreground">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : item.icon}
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold">{item.name}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {item.description}
          </p>
        </div>
      </div>
      <IntegrationStatusBadge status={item.status} />
    </div>

    <div className="mt-4 flex flex-wrap gap-2">
      {item.metadata.map((metadata) => (
        <Badge key={metadata} variant="outline" className="font-normal">
          {metadata}
        </Badge>
      ))}
    </div>

    {error && (
      <div className="mt-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    )}

    <div className="mt-auto flex items-center justify-between gap-3 pt-5">
      <Button
        variant="outline"
        size="sm"
        onClick={onManage}
        disabled={item.status === "coming-soon"}
      >
        {item.id === "homeassistant" ? "Manage" : "View"}
      </Button>
      {onRefresh && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      )}
    </div>
  </div>
);

const IntegrationStatusBadge: React.FC<{
  status: IntegrationRegistryItem["status"];
}> = ({ status }) => {
  if (status === "configured") {
    return <Badge>Configured</Badge>;
  }

  if (status === "available") {
    return <Badge variant="secondary">Available</Badge>;
  }

  return <Badge variant="outline">Coming soon</Badge>;
};

const getHomeAssistantEntityFriendlyName = (entity: HAEntity): string =>
  typeof entity.attributes.friendly_name === "string"
    ? entity.attributes.friendly_name
    : entity.entity_id;

const isHomeAssistantEntityControllable = (entity: HAEntity): boolean =>
  entity.domain === "light" || entity.domain === "switch";

const supportsHomeAssistantBrightness = (entity: HAEntity): boolean => {
  if (entity.domain !== "light") {
    return false;
  }

  if (typeof entity.attributes.brightness === "number") {
    return true;
  }

  const supportedColorModes = entity.attributes.supported_color_modes;
  return (
    Array.isArray(supportedColorModes) &&
    supportedColorModes.some(
      (mode) => typeof mode === "string" && mode !== "onoff",
    )
  );
};

const getHomeAssistantBrightnessPercent = (entity: HAEntity): number => {
  const brightness = entity.attributes.brightness;
  if (typeof brightness === "number" && Number.isFinite(brightness)) {
    return Math.max(1, Math.min(100, Math.round((brightness / 255) * 100)));
  }

  return entity.state === "on" ? 100 : 50;
};

type HAEntityControlFilter = "all" | "controllable" | "read-only";

const HomeAssistantManagementShell: React.FC<{
  organization: OrganizationDetails;
  onBack: () => void;
}> = ({ organization, onBack }) => {
  const [instances, setInstances] = useState<HAInstance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationToken, setRegistrationToken] = useState<string | null>(
    null,
  );
  const [selectedEntityInstance, setSelectedEntityInstance] =
    useState<HAInstance | null>(null);
  const [entities, setEntities] = useState<HAEntity[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [entitySearch, setEntitySearch] = useState("");
  const [entityDomainFilter, setEntityDomainFilter] = useState("all");
  const [entityControlFilter, setEntityControlFilter] =
    useState<HAEntityControlFilter>("all");
  const [entityNames, setEntityNames] = useState<Record<string, HAEntityName>>(
    {},
  );
  const [entityNameActionKey, setEntityNameActionKey] = useState<string | null>(
    null,
  );
  const [entityBrightness, setEntityBrightness] = useState<
    Record<string, number>
  >({});
  const [entityActionKey, setEntityActionKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadInstances = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setInstances(await getInstances());
    } catch (loadError: unknown) {
      setError(
        errorUtils.getApiErrorMessage(
          loadError,
          "Failed to load Home Assistant instances",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInstances();
  }, [loadInstances, organization.slug]);

  const loadEntities = useCallback(async (instance: HAInstance) => {
    setSelectedEntityInstance(instance);
    setIsLoadingEntities(true);
    setEntityError(null);
    try {
      const [loadedEntities, loadedNames] = await Promise.all([
        getEntities(instance._id),
        getEntityNames(instance._id),
      ]);
      setEntityNames(
        Object.fromEntries(
          loadedNames.map((name) => [name.entityId, name] as const),
        ),
      );
      setEntities(loadedEntities);
      setEntityBrightness((currentBrightness) => {
        const nextBrightness = { ...currentBrightness };
        for (const entity of loadedEntities) {
          if (
            supportsHomeAssistantBrightness(entity) &&
            nextBrightness[entity.entity_id] === undefined
          ) {
            nextBrightness[entity.entity_id] =
              getHomeAssistantBrightnessPercent(entity);
          }
        }
        return nextBrightness;
      });
    } catch (loadError: unknown) {
      setEntityError(
        errorUtils.getApiErrorMessage(
          loadError,
          "Failed to load Home Assistant entities",
        ),
      );
    } finally {
      setIsLoadingEntities(false);
    }
  }, []);

  const handleSetEntityName = async (entity: HAEntity) => {
    if (!selectedEntityInstance) {
      return;
    }

    const existingName =
      entityNames[entity.entity_id]?.displayName ??
      getHomeAssistantEntityFriendlyName(entity);
    const nextName = window.prompt(
      "Set the TerraMatrix-supported name for this entity. This does not rename it in Home Assistant.",
      existingName,
    );
    if (nextName === null) {
      return;
    }

    const trimmedName = nextName.trim();
    if (!trimmedName || trimmedName === entityNames[entity.entity_id]?.displayName) {
      return;
    }

    setEntityNameActionKey(entity.entity_id);
    setEntityError(null);
    try {
      const savedName = await setEntityName(
        selectedEntityInstance._id,
        entity.entity_id,
        trimmedName,
        entityNames[entity.entity_id]?.semanticType,
      );
      setEntityNames((currentNames) => ({
        ...currentNames,
        [savedName.entityId]: savedName,
      }));
      toast.success(`Saved TerraMatrix name for ${entity.entity_id}`);
    } catch (nameError: unknown) {
      setEntityError(
        errorUtils.getApiErrorMessage(
          nameError,
          "Failed to save TerraMatrix entity name",
        ),
      );
    } finally {
      setEntityNameActionKey(null);
    }
  };

  const handleClearEntityName = async (entity: HAEntity) => {
    if (!selectedEntityInstance || !entityNames[entity.entity_id]) {
      return;
    }

    setEntityNameActionKey(entity.entity_id);
    setEntityError(null);
    try {
      await deleteEntityName(selectedEntityInstance._id, entity.entity_id);
      setEntityNames((currentNames) => {
        const nextNames = { ...currentNames };
        delete nextNames[entity.entity_id];
        return nextNames;
      });
      toast.success(`Cleared TerraMatrix name for ${entity.entity_id}`);
    } catch (nameError: unknown) {
      setEntityError(
        errorUtils.getApiErrorMessage(
          nameError,
          "Failed to clear TerraMatrix entity name",
        ),
      );
    } finally {
      setEntityNameActionKey(null);
    }
  };

  const handleRegisterBridge = async () => {
    setIsRegistering(true);
    setError(null);
    try {
      const registration = await registerInstance(
        `${organization.name} Home Assistant`,
      );
      setInstances((currentInstances) => [
        registration.instance,
        ...currentInstances.filter(
          (instance) => instance._id !== registration.instance._id,
        ),
      ]);
      setRegistrationToken(registration.cloudToken);
    } catch (registerError: unknown) {
      setError(
        errorUtils.getApiErrorMessage(
          registerError,
          "Failed to register Home Assistant bridge",
        ),
      );
    } finally {
      setIsRegistering(false);
    }
  };

  const handleCopyRegistrationToken = async () => {
    if (!registrationToken || !navigator.clipboard) {
      return;
    }

    await navigator.clipboard.writeText(registrationToken);
    toast.success("Bridge token copied");
  };

  const handleEntityControl = async (
    entity: HAEntity,
    service: "turn_on" | "turn_off",
    data?: Record<string, unknown>,
  ) => {
    if (!selectedEntityInstance || !isHomeAssistantEntityControllable(entity)) {
      return;
    }

    const actionKey = `${entity.entity_id}:${service}`;
    setEntityActionKey(actionKey);
    setEntityError(null);
    try {
      await callService(
        selectedEntityInstance._id,
        entity.domain,
        service,
        entity.entity_id,
        data,
      );
      toast.success(
        `Sent ${service.replace("_", " ")} to ${getHomeAssistantEntityFriendlyName(
          entity,
        )}`,
      );
      await loadEntities(selectedEntityInstance);
    } catch (controlError: unknown) {
      setEntityError(
        errorUtils.getApiErrorMessage(
          controlError,
          "Failed to control Home Assistant entity",
        ),
      );
    } finally {
      setEntityActionKey(null);
    }
  };

  const onlineCount = instances.filter(
    (instance) => instance.status === "online",
  ).length;
  const entityCount = instances.reduce(
    (total, instance) => total + (instance.entityCount || 0),
    0,
  );
  const availableDomains = Array.from(
    new Set(entities.map((entity) => entity.domain).filter(Boolean)),
  ).sort((left, right) => left.localeCompare(right));
  const normalizedSearch = entitySearch.trim().toLowerCase();
  const filteredEntities = entities.filter((entity) => {
    const matchesDomain =
      entityDomainFilter === "all" || entity.domain === entityDomainFilter;
    const isControllable = isHomeAssistantEntityControllable(entity);
    const matchesControl =
      entityControlFilter === "all" ||
      (entityControlFilter === "controllable" && isControllable) ||
      (entityControlFilter === "read-only" && !isControllable);
    if (!matchesDomain || !matchesControl) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    const friendlyName =
      typeof entity.attributes.friendly_name === "string"
        ? entity.attributes.friendly_name
        : "";
    const terraMatrixName = entityNames[entity.entity_id]?.displayName ?? "";
    const areaName = entity.area?.name ?? "";
    const deviceName = entity.device?.name ?? "";
    return [
      entity.entity_id,
      friendlyName,
      terraMatrixName,
      areaName,
      deviceName,
      entity.state,
    ]
      .join(" ")
      .toLowerCase()
      .includes(normalizedSearch);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to integrations
          </Button>
          <div className="mt-3 flex items-center gap-2">
            <Home className="h-5 w-5 text-primary" />
            <Badge variant="outline">{organization.name}</Badge>
          </div>
          <h2 className="mt-2 text-2xl font-bold">Home Assistant</h2>
          <p className="text-muted-foreground">
            Manage Home Assistant instances, entities, permissions, and
            diagnostics for this organization.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadInstances}
          disabled={isLoading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryMetricCard
          label="Instances"
          value={String(instances.length)}
          detail={`${onlineCount} online`}
        />
        <SummaryMetricCard
          label="Entities"
          value={String(entityCount)}
          detail="Across connected instances"
        />
        <SummaryMetricCard
          label="Scope"
          value="Org"
          detail="Backend-enforced org boundary"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wifi className="h-5 w-5" />
            Instances
          </CardTitle>
          <CardDescription>
            Home Assistant bridges registered to {organization.name}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading Home Assistant...
            </div>
          ) : instances.length === 0 ? (
            <div className="space-y-4 rounded-md border border-dashed p-6">
              <div>
                <p className="font-medium">No bridge registered yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Register a Garcia House bridge, then restart the local Home
                  Assistant bridge with the one-time cloud token.
                </p>
              </div>
              <Button onClick={handleRegisterBridge} disabled={isRegistering}>
                {isRegistering && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Register bridge
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {instances.map((instance) => (
                <div
                  key={instance._id}
                  className="flex flex-col gap-3 rounded-md border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {instance.instanceName}
                      </span>
                      <Badge
                        variant={
                          instance.status === "online"
                            ? "default"
                            : instance.status === "pending"
                              ? "secondary"
                              : "outline"
                        }
                        className="capitalize"
                      >
                        {instance.status}
                      </Badge>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {instance.entityCount} entities - Last seen{" "}
                      {formatRelativeIntegrationDate(instance.lastSeen)}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEntityDomainFilter("all");
                      setEntityControlFilter("all");
                      setEntitySearch("");
                      void loadEntities(instance);
                    }}
                    disabled={instance.status !== "online" || isLoadingEntities}
                  >
                    <ListFilter className="mr-2 h-4 w-4" />
                    List entities
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {registrationToken && (
        <Card>
          <CardHeader>
            <CardTitle>Bridge pairing token</CardTitle>
            <CardDescription>
              This token is shown once. Use it as the bridge `CLOUD_TOKEN`, then
              restart the local bridge.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="break-all rounded-md bg-muted p-3 font-mono text-sm">
              {registrationToken}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void handleCopyRegistrationToken();
              }}
            >
              Copy token
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedEntityInstance && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ListFilter className="h-5 w-5" />
                  Entities
                </CardTitle>
                <CardDescription>
                  {selectedEntityInstance.instanceName} entities from Home
                  Assistant.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void loadEntities(selectedEntityInstance);
                }}
                disabled={isLoadingEntities}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${
                    isLoadingEntities ? "animate-spin" : ""
                  }`}
                />
                Refresh entities
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[1fr_220px]">
              <Input
                value={entitySearch}
                onChange={(event) => setEntitySearch(event.target.value)}
                placeholder="Search entity id, name, area, device, or state"
              />
              <Select
                value={entityDomainFilter}
                onValueChange={setEntityDomainFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Domain" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All domains</SelectItem>
                  {availableDomains.map((domain) => (
                    <SelectItem key={domain} value={domain}>
                      {domain}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={entityControlFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setEntityControlFilter("all")}
              >
                All entities
              </Button>
              <Button
                variant={
                  entityControlFilter === "controllable"
                    ? "default"
                    : "outline"
                }
                size="sm"
                onClick={() => setEntityControlFilter("controllable")}
              >
                Controllable only
              </Button>
              <Button
                variant={
                  entityControlFilter === "read-only" ? "default" : "outline"
                }
                size="sm"
                onClick={() => setEntityControlFilter("read-only")}
              >
                Read-only only
              </Button>
            </div>

            {entityError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{entityError}</span>
              </div>
            )}

            {isLoadingEntities ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading entities...
              </div>
            ) : filteredEntities.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
                No entities match the current filters.
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entity</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Control</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEntities.slice(0, 100).map((entity) => {
                      const homeAssistantName =
                        getHomeAssistantEntityFriendlyName(entity);
                      const terraMatrixName =
                        entityNames[entity.entity_id]?.displayName;
                      const displayName =
                        terraMatrixName ?? homeAssistantName;
                      const isControllable =
                        isHomeAssistantEntityControllable(entity);
                      const brightnessSupported =
                        supportsHomeAssistantBrightness(entity);
                      const brightnessValue =
                        entityBrightness[entity.entity_id] ??
                        getHomeAssistantBrightnessPercent(entity);
                      const isEntityActionPending =
                        entityActionKey?.startsWith(`${entity.entity_id}:`) ??
                        false;
                      return (
                        <TableRow key={entity.entity_id}>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">
                                {displayName}
                              </span>
                              {terraMatrixName && (
                                <Badge variant="secondary">TerraMatrix</Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {terraMatrixName && `HA: ${homeAssistantName} - `}
                              {entity.entity_id}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  void handleSetEntityName(entity);
                                }}
                                disabled={
                                  entityNameActionKey === entity.entity_id
                                }
                              >
                                {terraMatrixName
                                  ? "Edit TerraMatrix name"
                                  : "Set TerraMatrix name"}
                              </Button>
                              {terraMatrixName && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => {
                                    void handleClearEntityName(entity);
                                  }}
                                  disabled={
                                    entityNameActionKey === entity.entity_id
                                  }
                                >
                                  Clear
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{entity.domain}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {entity.state}
                          </TableCell>
                          <TableCell>{entity.area?.name ?? "-"}</TableCell>
                          <TableCell>{entity.device?.name ?? "-"}</TableCell>
                          <TableCell>
                            {isControllable ? (
                              <div className="flex min-w-[220px] flex-col gap-2">
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      void handleEntityControl(
                                        entity,
                                        "turn_on",
                                      );
                                    }}
                                    disabled={isEntityActionPending}
                                    aria-label={`Turn on ${displayName}`}
                                  >
                                    On
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      void handleEntityControl(
                                        entity,
                                        "turn_off",
                                      );
                                    }}
                                    disabled={isEntityActionPending}
                                    aria-label={`Turn off ${displayName}`}
                                  >
                                    Off
                                  </Button>
                                </div>
                                {brightnessSupported && (
                                  <div className="flex items-center gap-3">
                                    <Slider
                                      min={1}
                                      max={100}
                                      step={1}
                                      value={[brightnessValue]}
                                      disabled={isEntityActionPending}
                                      aria-label={`Set brightness for ${displayName}`}
                                      onValueChange={(value) => {
                                        const [nextValue] = value;
                                        if (typeof nextValue === "number") {
                                          setEntityBrightness((current) => ({
                                            ...current,
                                            [entity.entity_id]: nextValue,
                                          }));
                                        }
                                      }}
                                      onValueCommit={(value) => {
                                        const [nextValue] = value;
                                        if (typeof nextValue === "number") {
                                          void handleEntityControl(
                                            entity,
                                            "turn_on",
                                            { brightness_pct: nextValue },
                                          );
                                        }
                                      }}
                                    />
                                    <span className="w-10 text-right text-xs text-muted-foreground">
                                      {brightnessValue}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Read only
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {filteredEntities.length > 100 && (
                  <div className="border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
                    Showing first 100 of {filteredEntities.length} matching
                    entities. Narrow the search or domain filter to see more.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const SummaryMetricCard: React.FC<{
  label: string;
  value: string;
  detail: string;
}> = ({ label, value, detail }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardDescription>{label}</CardDescription>
      <CardTitle className="text-2xl">{value}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-sm text-muted-foreground">{detail}</p>
    </CardContent>
  </Card>
);

const IntegrationPlaceholder: React.FC<{
  icon: React.ReactNode;
  label: string;
}> = ({ icon, label }) => (
  <div className="flex items-center gap-3 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
    {icon}
    {label}
  </div>
);

export default OrgSettingsPage;
